import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { naviguer } from '../../App';
import type { Demande } from '../../types';
import { formatDateCourt } from './format';

// « 14:00:00 » → « 14 h » ; « 09:30:00 » → « 9 h 30 »
function heure(t?: string | null): string {
  const m = t ? /^(\d{1,2}):(\d{2})/.exec(t) : null;
  if (!m) return '';
  return m[2] === '00' ? `${Number(m[1])} h` : `${Number(m[1])} h ${m[2]}`;
}

function horaire(d: Demande): string {
  const a = heure(d.atelier_heure_debut);
  const b = heure(d.atelier_heure_fin);
  return a && b ? `${a} – ${b}` : '';
}

// Où en est un remplacement déjà confirmé, dit à la personne qui remplace.
function etape(d: Demande): string {
  switch (d.statut) {
    case 'validation_responsable': return "Validation en cours par l'équipe gestion";
    case 'validation_rh': return 'Validation en cours par les RH';
    case 'attente_tipee': return "Les RH valident l'absence dans Tipee";
    case 'passation': return `Passation à préparer avec ${d.absent_nom ?? 'la personne absente'}`;
    default: return '';
  }
}

type Choix = { demande: Demande; action: 'confirmer_remplacement' | 'refuser_remplacement' } | null;

/**
 * Bloc « Vos remplacements » du Tableau de bord, au-dessus des onglets : les
 * séances où la personne connectée est désignée remplaçant·e. « À confirmer »
 * demande une réponse (Je confirme / Décliner, sans quitter le tableau de
 * bord) ; « À venir » suit les remplacements confirmés. N'affiche rien tant
 * qu'il n'y a rien à montrer. Voir docs/DECISIONS.md §14.
 */
export function VosRemplacements({ rechargerCle, onChange }: { rechargerCle?: string; onChange?: () => void }) {
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [choix, setChoix] = useState<Choix>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(() => {
    return api.getMesRemplacements().then(setDemandes).catch(() => setDemandes([]));
  }, []);
  useEffect(() => { void charger(); }, [charger, rechargerCle]);

  // Échap ferme la fenêtre (jamais un clic à côté).
  useEffect(() => {
    if (!choix) return;
    const f = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enCours) setChoix(null); };
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
  }, [choix, enCours]);

  if (demandes.length === 0 && !choix) return null;

  const aConfirmer = demandes.filter((d) => d.statut === 'attente_confirmation_remplacant');
  const aVenir = demandes.filter((d) => d.statut !== 'attente_confirmation_remplacant');

  async function repondre() {
    if (!choix) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.appliquerAction(choix.demande.id, choix.action);
      setChoix(null);
      await charger();
      onChange?.();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "La réponse n'a pas pu être enregistrée. Réessayez.");
    } finally {
      setEnCours(false);
    }
  }

  const seance = (d: Demande) => [formatDateCourt(d.date_atelier), horaire(d)].filter(Boolean).join(' · ');
  const titre = (d: Demande) => (d.atelier_lieu ? `${d.atelier_nom} · ${d.atelier_lieu}` : d.atelier_nom);

  return (
    <div className="card mb-16" style={{ display: 'grid', gap: 14 }} aria-label="Vos remplacements">
      {demandes.length > 0 && (
        <div className="font-medium text-ink-2" style={{ fontSize: 'var(--t-12, 12px)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Vos remplacements
        </div>
      )}

      {aConfirmer.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="font-medium">À confirmer <span className="text-ink-3">{aConfirmer.length}</span></div>
          {aConfirmer.map((d) => (
            <div key={d.id} style={{ border: '1px solid var(--accent)', borderLeft: '4px solid var(--accent)', background: 'var(--accent-dim)', borderRadius: 'var(--r1)', padding: '10px 12px', display: 'grid', gap: 6 }}>
              <div className="font-medium">On vous propose de remplacer {d.absent_nom ?? 'un·e collègue'}</div>
              <div className="text-sm text-ink-2">{titre(d)} · {seance(d)}</div>
              {d.designe_par_nom && <div className="text-sm text-ink-2">Désigné·e par {d.designe_par_nom}</div>}
              <div className="flex gap-8" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-accent" onClick={() => { setErreur(null); setChoix({ demande: d, action: 'confirmer_remplacement' }); }}>Je confirme</button>
                <button type="button" className="btn" onClick={() => { setErreur(null); setChoix({ demande: d, action: 'refuser_remplacement' }); }}>Décliner</button>
                <button type="button" className="rech-toggle-link" onClick={() => naviguer('demandes', d.id)}>Voir la fiche</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {aVenir.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="font-medium">À venir <span className="text-ink-3">{aVenir.length}</span></div>
          {aVenir.map((d) => (
            <div key={d.id} className="flex gap-12" style={{ flexWrap: 'wrap', alignItems: 'baseline', border: '1px solid var(--line)', borderRadius: 'var(--r1)', padding: '8px 12px', fontSize: 'var(--t-13)' }}>
              <span className="font-medium">{titre(d)}</span>
              <span className="text-ink-3">{seance(d)}</span>
              <span>Vous remplacez {d.absent_nom ?? 'un·e collègue'}</span>
              <span className="text-ink-2">{etape(d)}</span>
              <button type="button" className="rech-toggle-link" style={{ marginLeft: 'auto' }} onClick={() => naviguer('demandes', d.id)}>Voir la fiche</button>
            </div>
          ))}
        </div>
      )}

      {choix && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {choix.action === 'confirmer_remplacement' ? 'Confirmer ce remplacement ?' : 'Décliner ce remplacement ?'}
              </h3>
              <button type="button" className="close-btn" onClick={() => setChoix(null)} aria-label="Fermer" disabled={enCours}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'var(--bg-inset)', borderRadius: 'var(--r1)', padding: '10px 12px', marginBottom: 12 }}>
                <div className="font-medium">
                  {choix.action === 'confirmer_remplacement'
                    ? `Vous remplacerez ${choix.demande.absent_nom ?? 'un·e collègue'}`
                    : titre(choix.demande)}
                </div>
                <div className="text-sm text-ink-2">
                  {choix.action === 'confirmer_remplacement'
                    ? `${titre(choix.demande)} · ${seance(choix.demande)}`
                    : `${seance(choix.demande)} · absence de ${choix.demande.absent_nom ?? 'un·e collègue'}`}
                </div>
              </div>
              <p className="text-sm text-ink-2">
                {choix.action === 'confirmer_remplacement'
                  ? "L'équipe gestion validera ensuite le remplacement. Vous serez prévenu·e."
                  : `${choix.demande.absent_nom ?? 'La personne absente'} sera prévenu·e et la recherche reprendra. Le remplacement disparaîtra de votre liste.`}
              </p>
              {erreur && <p className="text-sm" style={{ color: 'var(--danger)', marginTop: 12 }}>{erreur}</p>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setChoix(null)} disabled={enCours}>Retour</button>
              <button
                type="button"
                className={`btn ${choix.action === 'confirmer_remplacement' ? 'btn-accent' : 'btn-danger'}`}
                onClick={repondre}
                disabled={enCours}
              >
                {enCours ? <span className="spinner spinner-sm" /> : choix.action === 'confirmer_remplacement' ? 'Oui, je confirme' : 'Oui, décliner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
